import { useState } from 'react';
import { PasswordField } from './PasswordField';
import { isTrustedDevice, tokenSubject } from '../trustedDevices';
import type { ConnectOptions } from '../sessions/types';

/**
 * 목록에서 접속할 때 **접속 비밀번호를 받는 공통 조각**(기기 목록·주소록에서 재사용).
 *
 * 🔴 왜 필요한가 (2026-09-14 실기에서 드러남)
 * 목록의 [접속] 은 비밀번호를 **한 번도 보내지 않았다**. 피제어 기기의 승인 판정은 전적으로 그 기기가 하는데
 * (세션 비밀번호 / 무인 비밀번호 / 사람이 수락), 무인 보드처럼 **앞에 사람이 없는 기기는 수락할 수가 없어**
 * 무조건 거절된다 — 화면에는 "접속이 거부되었습니다. 접속 비밀번호를 확인하세요" 만 뜨고, 정작 그 비밀번호를
 * 넣을 칸이 목록 어디에도 없었다(직접 접속 화면에만 있었다).
 *
 * ⚠️ **비워 둘 수 있어야 한다.** 사람이 앞에 있는 PC 는 비밀번호 없이 접속하면 그쪽에 승인 창이 뜨는 것이
 * 정상 흐름이다. 필수로 만들면 그 경로가 사라진다.
 */
export type ConnectPasswordControl = ReturnType<typeof useConnectPassword>;

/**
 * 어느 항목에 대해 비밀번호를 묻는 중인지 + 입력값을 관리한다.
 *
 * 세션 창에 보일 **이름**도 함께 들고 간다 — 기기 목록은 `agentId`(UUID)로 접속하므로, 이름을 넘기지 않으면
 * 세션 창 제목이 `ddd5c007-5535-…` 같은 날것이 된다(2026-09-14 화면에서 그대로 보였다).
 */
export function useConnectPassword(
  onConnect: (target: string, password?: string, label?: string, opts?: ConnectOptions) => void,
  /** 지금 로그인 토큰(계정별 "다음부터 묻지 않고 접속" 기억의 기준 — trustedDevices.ts). */
  getToken?: () => string,
) {
  /** 비밀번호를 묻는 중인 대상(기기 목록은 agentId, 주소록은 기기 코드). 빈 값 = 묻는 중 아님. */
  const [target, setTarget] = useState('');
  const [label, setLabel] = useState<string | undefined>(undefined);
  const [value, setValue] = useState('');
  /** "다음부터 묻지 않고 접속" 체크. 비밀번호를 넣었을 때만 뜻이 있다. */
  const [remember, setRemember] = useState(false);

  const user = () => tokenSubject(getToken?.());

  /**
   * [접속]. 이 기기가 **이 계정을 이미 비밀번호 없이 허용**했다고 기억하면 묻지 않고 바로 접속한다.
   * (허용이 거둬졌으면 기기가 거부하고, 세션 쪽이 기억을 지운다 → 다음 [접속]부터 다시 묻는다.)
   */
  const ask = (t: string, name?: string) => {
    if (isTrustedDevice(user(), t)) {
      onConnect(t, undefined, name, { trusted: true });
      return;
    }
    setTarget(t);
    setLabel(name);
    setValue('');
    setRemember(false);
  };
  const cancel = () => {
    setTarget('');
    setLabel(undefined);
    setValue('');
    setRemember(false);
  };
  const submit = () => {
    if (!target) return;
    const t = target;
    const name = label;
    const pw = value;
    const rem = remember && !!pw;
    cancel();
    // 빈 값이면 `undefined` 로 넘긴다 — 빈 문자열을 보내면 "비밀번호를 시도했지만 틀렸다" 와 구분되지 않는다.
    onConnect(t, pw || undefined, name, rem ? { remember: true } : undefined);
  };

  return { target, value, setValue, remember, setRemember, canRemember: !!getToken, ask, cancel, submit };
}

/** 비밀번호 입력 칸 + [연결]/[취소]. 목록 행의 버튼 자리에 그대로 들어간다. */
export function ConnectPasswordFields({
  ctl,
  busy,
}: {
  ctl: ConnectPasswordControl;
  busy: boolean;
}) {
  return (
    <>
      <PasswordField
        autoFocus
        value={ctl.value}
        onChange={ctl.setValue}
        placeholder="세션/무인 비밀번호 (비우면 상대 승인)"
        style={{ width: 260 }}
        aria-label="접속 비밀번호"
        onKeyDown={(e) => {
          if (e.key === 'Enter') ctl.submit();
          if (e.key === 'Escape') ctl.cancel();
        }}
      />
      {/* "다음부터 묻지 않고 접속" — 기기가 **무인 비밀번호로** 승인할 때만 허용이 생긴다(세션 비밀번호로는 안 된다).
          허용은 그 기기에서 무인 비밀번호를 바꾸거나 [모두 해제]하면 풀린다. 비밀번호는 어디에도 저장하지 않는다. */}
      {ctl.canRemember && (
        <label
          className="remember-device"
          title="무인 비밀번호로 접속하면 이 기기가 내 계정을 기억해, 다음부터 비밀번호 없이 접속합니다. 비밀번호는 저장하지 않습니다."
        >
          <input
            type="checkbox"
            checked={ctl.remember}
            disabled={!ctl.value}
            onChange={(e) => ctl.setRemember(e.target.checked)}
          />
          다음부터 묻지 않고 접속
        </label>
      )}
      <button onClick={ctl.submit} disabled={busy}>
        연결
      </button>
      <button className="ghost" onClick={ctl.cancel}>
        취소
      </button>
    </>
  );
}
